/**
 * Use this file for JavaScript code that you want to run in the front-end 
 * on posts/pages that contain this block.
 *
 * When this file is defined as the value of the `viewScript` property
 * in `block.json` it will be enqueued on the front end of the site.
 *
 * Example:
 *
 * ```js
 * {
 *   "viewScript": "file:./view.js"
 * }
 * ```
 *
 * If you're not making any changes to this file because your project doesn't need any 
 * JavaScript running in the front-end, then you should delete this file and remove 
 * the `viewScript` property from `block.json`. 
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-metadata/#view-script
 */

/* eslint-disable no-console */
// console.log("Hello World! (from ultime-blocks-sql-subquery-visualizer-fr block)");
/* eslint-enable no-console */

(function () {
    // ── Données ────────────────────────────────────────────
    const employes = [
        { id: 1, nom: "Alice", departement: "Ventes", salaire: 2800 },
        { id: 2, nom: "Bob", departement: "Tech", salaire: 3500 },
        { id: 3, nom: "Claire", departement: "Ventes", salaire: 3100 },
        { id: 4, nom: "David", departement: "Interim", salaire: 1800 },
        { id: 5, nom: "Emma", departement: "Ventes", salaire: 2600 },
        { id: 6, nom: "Farid", departement: "Interim", salaire: 1900 },
        { id: 7, nom: "Gaëlle", departement: "Tech", salaire: 4000 },
        { id: 8, nom: "Hugo", departement: "Interim", salaire: 2100 }
    ];

    const commandes = [
        { id: 1, client_id: 1, produit: "Stylo", montant: 12 },
        { id: 2, client_id: 3, produit: "Cahier", montant: 8 },
        { id: 3, client_id: 1, produit: "Règle", montant: 5 },
        { id: 4, client_id: 7, produit: "Crayon", montant: 3 },
        { id: 5, client_id: 3, produit: "Gomme", montant: 2 },
        { id: 6, client_id: 2, produit: "Stylo", montant: 12 }
    ];

    // ── Helpers ────────────────────────────────────────────
    function tableHtml(data, cols, stateMap) {
        const thead = cols.map(c => `<th>${c}</th>`).join("");
        const rows = data.map(r => {
            const cls = stateMap ? (stateMap[r.id] || "") : "";
            return `<tr class="${cls}">${cols.map(c => `<td>${r[c] !== undefined ? r[c] : "NULL"}</td>`).join("")}</tr>`;
        }).join("");
        return `<table class="mini"><thead><tr>${thead}</tr></thead><tbody>${rows}</tbody></table>`;
    }

    function blockHtml(headerCls, headerLabel, tableContent) {
        return `<div class="table-block">
            <div class="table-block-header ${headerCls}">${headerLabel}</div>
            <div class="table-block-body">${tableContent}</div>
        </div>`;
    }

    function legendHtml(items) {
        if (!items.length) return "";
        const spans = items.map(([color, label]) =>
            `<span><i class="legend-dot" style="background:${color};border:1px solid #555;"></i> ${label}</span>`
        ).join("");
        return `<div class="legend">${spans}</div>`;
    }

    // ── Définition des 4 requêtes ──────────────────────────
    const queries = {

        // ── WHERE IN ──────────────────────────────────────
        in: {
            parts: [
                { id: "in-select", text: "SELECT nom, salaire" },
                { id: "in-from", text: "FROM employes" },
                { id: "in-where", text: "WHERE id IN", cls: "" },
                { id: "in-sub", text: "    (SELECT client_id FROM commandes)", cls: "subquery" },
                { id: "in-semi", text: ";" }
            ],
            steps: [
                {
                    key: "in-sub",
                    sql: "Exécution de la sous-requête",
                    desc: "Le moteur exécute <strong>d'abord</strong> la sous-requête : <code>SELECT client_id FROM commandes</code>.",
                    render: () => {
                        const subResult = [...new Set(commandes.map(c => c.client_id))].map(id => ({ client_id: id }));
                        return `<div class="tables-grid">
                            ${blockHtml("sub", "📋 Table : commandes", tableHtml(commandes, ["id", "client_id", "produit", "montant"], null))}
                            ${blockHtml("sub", "⚡ Résultat sous-requête — valeurs de client_id", tableHtml(subResult, ["client_id"], null))}
                        </div>
                        ${legendHtml([["#fef3c7", "Sous-requête en cours d'exécution"]])}`;
                    }
                },
                {
                    key: "in-from",
                    sql: "Lecture de la table principale",
                    desc: "La sous-requête a retourné les ids <code>[1, 3, 7, 2]</code>. Le moteur lit maintenant <code>employes</code> et compare chaque <code>id</code> avec cette liste.",
                    render: () => {
                        const ids = [...new Set(commandes.map(c => c.client_id))];
                        const idsLabel = ids.join(", ");
                        const stateMap = {};
                        employes.forEach(e => { stateMap[e.id] = ids.includes(e.id) ? "matched" : "unmatched"; });
                        const subResult = ids.map(id => ({ id, client_id: id }));
                        return `<div class="tables-grid">
                                ${blockHtml("main", "📋 Table : employes — vérification WHERE id IN [" + idsLabel + "]", tableHtml(employes, ["id", "nom", "departement", "salaire"], stateMap))}
                                ${blockHtml("sub", "⚡ Résultat sous-requête injecté", tableHtml(ids.map(id => ({ client_id: id })).map((r, i) => ({ ...r, id: i })), ["client_id"], null))}
                            </div>
                            ${legendHtml([["#86efac", "id présent dans la sous-requête → conservé"], ["#f1f5f9", "id absent → ignoré"]])}`;
                    }
                },
                {
                    key: "in-select",
                    sql: "SELECT — résultat final",
                    desc: "Seules les lignes dont l'<code>id</code> est dans la liste sont conservées. Les colonnes <code>nom</code> et <code>salaire</code> sont projetées.",
                    render: () => {
                        const ids = [...new Set(commandes.map(c => c.client_id))];
                        const result = employes.filter(e => ids.includes(e.id));
                        return `<div class="result-block">
                            ${blockHtml("result", "✅ Résultat final", tableHtml(result, ["id", "nom", "salaire"], null))}
                        </div>
                        ${legendHtml([["#86efac", "Lignes retournées par la requête"]])}`;
                    }
                }
            ]
        },

        // ── WHERE EXISTS ──────────────────────────────────
        exists: {
            parts: [
                { id: "ex-select", text: "SELECT nom, salaire" },
                { id: "ex-from", text: "FROM employes e" },
                { id: "ex-where", text: "WHERE EXISTS" },
                { id: "ex-sub", text: "    (SELECT 1 FROM commandes c\n     WHERE c.client_id = e.id)", cls: "subquery" },
                { id: "ex-semi", text: ";" }
            ],
            steps: [
                {
                    key: "ex-from",
                    sql: "Lecture de la table principale",
                    desc: "Le moteur parcourt chaque ligne d'<code>employes</code>. Pour chaque ligne, il va tester la condition EXISTS.",
                    render: () => {
                        return `<div class="tables-grid">
                            ${blockHtml("main", "📋 Table : employes — chaque ligne va être testée", tableHtml(employes, ["id", "nom", "departement", "salaire"], null))}
                            ${blockHtml("sub", "📋 Table : commandes — utilisée par la sous-requête", tableHtml(commandes, ["id", "client_id", "produit", "montant"], null))}
                        </div>`;
                    }
                },
                {
                    key: "ex-sub",
                    sql: "EXISTS — test pour chaque ligne",
                    desc: "Différence clé avec <code>WHERE IN</code> : EXISTS ne retourne <strong>pas de liste de valeurs</strong> — il teste juste si au moins une ligne existe. Il s'arrête dès qu'il en trouve une (<strong>court-circuit</strong>). Pour chaque employé, la sous-requête vérifie s'il existe au moins une commande avec <code>client_id = e.id</code>. Si oui → ligne conservée.",
                    render: () => {
                        const ids = [...new Set(commandes.map(c => c.client_id))];
                        const stateMap = {};
                        employes.forEach(e => { stateMap[e.id] = ids.includes(e.id) ? "matched" : "unmatched"; });
                        return `<div class="tables-grid">
                            ${blockHtml("main", "📋 employes — résultat du test EXISTS", tableHtml(employes, ["id", "nom", "departement", "salaire"], stateMap))}
                            ${blockHtml("sub", "📋 commandes — table testée par EXISTS", tableHtml(commandes, ["id", "client_id", "produit", "montant"], null))}
                        </div>
                        ${legendHtml([["#86efac", "EXISTS = TRUE → ligne conservée"], ["#f1f5f9", "EXISTS = FALSE → ligne ignorée"]])}`;
                    }
                },
                {
                    key: "ex-select",
                    sql: "SELECT — résultat final",
                    desc: "Seules les lignes pour lesquelles EXISTS a retourné TRUE sont dans le résultat final.",
                    render: () => {
                        const ids = [...new Set(commandes.map(c => c.client_id))];
                        const result = employes.filter(e => ids.includes(e.id));
                        return `<div class="result-block">
                            ${blockHtml("result", "✅ Résultat final", tableHtml(result, ["id", "nom", "salaire"], null))}
                        </div>`;
                    }
                }
            ]
        },

        // ── FROM sous-requête ─────────────────────────────
        from: {
            parts: [
                { id: "fr-select", text: "SELECT dept, avg_sal" },
                { id: "fr-from", text: "FROM" },
                { id: "fr-sub", text: "    (SELECT departement AS dept,\n            AVG(salaire) AS avg_sal\n     FROM employes\n     GROUP BY departement) AS stats", cls: "subquery" },
                { id: "fr-where", text: "WHERE avg_sal > 2500;" }
            ],
            steps: [
                {
                    key: "fr-sub",
                    sql: "Exécution de la sous-requête (table dérivée)",
                    desc: "La sous-requête est exécutée <strong>en premier</strong> et produit une table temporaire nommée <code>stats</code>.",
                    render: () => {
                        const stats = [];
                        const m = new Map();
                        employes.forEach(e => {
                            if (!m.has(e.departement)) m.set(e.departement, []);
                            m.get(e.departement).push(e.salaire);
                        });
                        m.forEach((salaires, dept) => {
                            const avg = Math.round(salaires.reduce((a, b) => a + b, 0) / salaires.length);
                            stats.push({ dept, avg_sal: avg, id: dept });
                        });
                        return `<div class="tables-grid">
                            ${blockHtml("main", "📋 Table source : employes", tableHtml(employes, ["id", "nom", "departement", "salaire"], null))}
                            ${blockHtml("sub", "⚡ Table dérivée : stats (résultat sous-requête)", tableHtml(stats, ["dept", "avg_sal"], null))}
                        </div>
                        ${legendHtml([["#fef3c7", "Table temporaire générée par la sous-requête"]])}`;
                    }
                },
                {
                    key: "fr-where",
                    sql: "WHERE avg_sal > 2500 — filtre sur la table dérivée",
                    desc: "La requête principale filtre les lignes de <code>stats</code> dont <code>avg_sal > 2500</code>.",
                    render: () => {
                        const stats = [];
                        const m = new Map();
                        employes.forEach(e => {
                            if (!m.has(e.departement)) m.set(e.departement, []);
                            m.get(e.departement).push(e.salaire);
                        });
                        m.forEach((salaires, dept) => {
                            const avg = Math.round(salaires.reduce((a, b) => a + b, 0) / salaires.length);
                            stats.push({ dept, avg_sal: avg, id: dept });
                        });
                        const stateMap = {};
                        stats.forEach(s => { stateMap[s.id] = s.avg_sal > 2500 ? "matched" : "unmatched"; });
                        return `<div class="tables-grid">
                            ${blockHtml("sub", "⚡ Table dérivée stats — filtre WHERE avg_sal > 2500", tableHtml(stats, ["dept", "avg_sal"], stateMap))}
                        </div>
                        ${legendHtml([["#86efac", "avg_sal > 2500 → conservé"], ["#f1f5f9", "avg_sal ≤ 2500 → ignoré"]])}`;
                    }
                },
                {
                    key: "fr-select",
                    sql: "SELECT — résultat final",
                    desc: "Les colonnes <code>dept</code> et <code>avg_sal</code> des lignes filtrées sont retournées.",
                    render: () => {
                        const m = new Map();
                        employes.forEach(e => {
                            if (!m.has(e.departement)) m.set(e.departement, []);
                            m.get(e.departement).push(e.salaire);
                        });
                        const result = [];
                        m.forEach((salaires, dept) => {
                            const avg = Math.round(salaires.reduce((a, b) => a + b, 0) / salaires.length);
                            if (avg > 2500) result.push({ dept, avg_sal: avg });
                        });
                        return `<div class="result-block">
                            ${blockHtml("result", "✅ Résultat final", tableHtml(result.map((r, i) => ({ ...r, id: i })), ["dept", "avg_sal"], null))}
                        </div>`;
                    }
                }
            ]
        },

        // ── Sous-requête scalaire ─────────────────────────
        scalar: {
            parts: [
                { id: "sc-select", text: "SELECT nom, salaire," },
                { id: "sc-sub", text: "       (SELECT AVG(salaire) FROM employes) AS salaire_moyen", cls: "subquery" },
                { id: "sc-from", text: "FROM employes" },
                { id: "sc-where", text: "WHERE salaire >", cls: "" },
                { id: "sc-sub2", text: "    (SELECT AVG(salaire) FROM employes);", cls: "subquery" }
            ],
            steps: [
                {
                    key: "sc-sub",
                    sql: "Exécution de la sous-requête scalaire",
                    desc: "La sous-requête <code>SELECT AVG(salaire) FROM employes</code> retourne <strong>une seule valeur</strong> (scalaire) : le salaire moyen.",
                    render: () => {
                        const avg = Math.round(employes.reduce((a, e) => a + e.salaire, 0) / employes.length);
                        return `<div class="tables-grid">
                            ${blockHtml("main", "📋 Table : employes", tableHtml(employes, ["id", "nom", "salaire"], null))}
                            ${blockHtml("sub", "⚡ Résultat scalaire", `<table class="mini"><thead><tr><th>AVG(salaire)</th></tr></thead><tbody><tr class="subresult"><td>${avg}</td></tr></tbody></table>`)}
                        </div>
                        ${legendHtml([["#fef08a", "Valeur scalaire retournée par la sous-requête"]])}`;
                    }
                },
                {
                    key: "sc-where",
                    sql: "WHERE salaire > AVG — comparaison ligne par ligne",
                    desc: "La valeur scalaire est injectée dans le WHERE. Chaque ligne est comparée à cette valeur.",
                    render: () => {
                        const avg = Math.round(employes.reduce((a, e) => a + e.salaire, 0) / employes.length);
                        const stateMap = {};
                        employes.forEach(e => { stateMap[e.id] = e.salaire > avg ? "matched" : "unmatched"; });
                        return `<div class="tables-grid">
                            ${blockHtml("main", `📋 employes — WHERE salaire > ${avg}`, tableHtml(employes, ["id", "nom", "salaire"], stateMap))}
                            ${blockHtml("sub", "⚡ Valeur injectée", `<table class="mini"><thead><tr><th>AVG(salaire)</th></tr></thead><tbody><tr class="subresult"><td>${avg}</td></tr></tbody></table>`)}
                        </div>
                        ${legendHtml([["#86efac", "salaire > moyenne → conservé"], ["#f1f5f9", "salaire ≤ moyenne → ignoré"]])}`;
                    }
                },
                {
                    key: "sc-select",
                    sql: "SELECT — résultat final avec colonne calculée",
                    desc: "Chaque ligne retournée affiche <code>nom</code>, <code>salaire</code> et la colonne calculée <code>salaire_moyen</code>.",
                    render: () => {
                        const avg = Math.round(employes.reduce((a, e) => a + e.salaire, 0) / employes.length);
                        const result = employes
                            .filter(e => e.salaire > avg)
                            .map(e => ({ nom: e.nom, salaire: e.salaire, salaire_moyen: avg }));
                        return `<div class="result-block">
                            ${blockHtml("result", "✅ Résultat final", tableHtml(result.map((r, i) => ({ ...r, id: i })), ["nom", "salaire", "salaire_moyen"], null))}
                        </div>`;
                    }
                }
            ]
        }
    };

    // ── État global ────────────────────────────────────────
    const tabConfig = {
        in: { cls: "active-in", query: queries.in },
        exists: { cls: "active-exists", query: queries.exists },
        from: { cls: "active-from", query: queries.from },
        scalar: { cls: "active-scalar", query: queries.scalar }
    };

    let activeKey = "in";
    let activeQuery = queries.in;
    let curStepIdx = -1;

    // ── Rendu SQL ──────────────────────────────────────────
    function renderSql() {
        document.getElementById("sql-display").innerHTML =
            activeQuery.parts.map(p =>
                `<span class="sql-part ${p.cls || ""}" id="${p.id}">${p.text}</span>`
            ).join("\n");
    }

    // ── Mise à jour de l'UI ────────────────────────────────
    function updateStep(i) {
        curStepIdx = i;
        const step = activeQuery.steps[i];

        document.querySelectorAll(".sql-part").forEach(p => p.classList.remove("active"));
        const el = document.getElementById(step.key);
        if (el) el.classList.add("active");

        document.getElementById("step-meta").innerHTML =
            `<strong>Étape ${i + 1} / ${activeQuery.steps.length} — ${step.sql}</strong><br>${step.desc}`;

        document.getElementById("step-output").innerHTML = step.render();
    }

    // ── Reset ──────────────────────────────────────────────
    function reset() {
        curStepIdx = -1;
        document.querySelectorAll(".sql-part").forEach(p => p.classList.remove("active"));
        document.getElementById("step-meta").textContent =
            "Cliquez sur un bouton pour avancer ou reculer dans l'exécution.";
        document.getElementById("step-output").innerHTML = "";
    }

    // ── Navigation ─────────────────────────────────────────
    document.getElementById("btn-next").addEventListener("click", () => {
        if (curStepIdx < activeQuery.steps.length - 1) updateStep(curStepIdx + 1);
    });

    document.getElementById("btn-prev").addEventListener("click", () => {
        if (curStepIdx > 0) updateStep(curStepIdx - 1);
    });

    document.getElementById("btn-reset").addEventListener("click", reset);

    // ── Bascule onglets ────────────────────────────────────
    Object.keys(tabConfig).forEach(key => {
        document.getElementById(`tab-${key}`).addEventListener("click", () => {
            activeKey = key;
            activeQuery = tabConfig[key].query;
            Object.keys(tabConfig).forEach(k => {
                document.getElementById(`tab-${k}`).className =
                    k === key ? `query-tab ${tabConfig[k].cls}` : "query-tab";
            });
            reset();
            renderSql();
        });
    });

    // ── Init ───────────────────────────────────────────────
    renderSql();
})()